import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import Select from './Select';
import BasicInput from './BasicInput';
import Checkbox from './Checkbox';
import closeIcon from '@/assets/close.svg';
import { validateAddressForm } from '@/utils/addressValidation';
import { getAddressByPostalCode } from '@/api/address';
import { useMetadata } from '@/hooks/useMetadata';

const AddressEditor = ({
  isOpen,
  onClose,
  onSave,
  address = null,
  isEditing = false,
  hideDefaultOption = false
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    lastNameKana: '',
    firstNameKana: '',
    phone: '',
    postalCode: '',
    prefecture: '',
    city: '',
    street: '',
    building: '',
    isDefault: false
  });
  const [errors, setErrors] = useState({});
  const [isAutoFilling, setIsAutoFilling] = useState(false);

  // Use shared metadata hook - data is prefetched on app start and cached for 1 hour
  const { data: metadata, isLoading: isLoadingPrefectures } = useMetadata();
  
  // Transform prefectures data to match expected format
  const prefectures = metadata?.prefectures?.map(prefecture => ({
    value: prefecture.name,
    label: prefecture.name
  })) || [];

  useEffect(() => {
    if (address && isEditing) {
      setFormData({
        lastName: address.lastname || '',
        firstName: address.firstname || '',
        lastNameKana: address.lastnameKana || '',
        firstNameKana: address.firstnameKana || '',
        phone: address.contactNumber || '',
        postalCode: address.postalCode || '',
        prefecture: address.prefecture || '',
        city: address.city || '',
        street: address.address1 || '',
        building: address.address2 || '',
        isDefault: address.isDefault || false
      });
    } else {
      // Reset form for new address
      setFormData({
        lastName: '',
        firstName: '',
        lastNameKana: '',
        firstNameKana: '',
        phone: '',
        postalCode: '',
        prefecture: '',
        city: '',
        street: '',
        building: '',
        isDefault: false
      });
    }
  }, [address, isEditing, isOpen]);

  const handleInputChange = (field, value) => {
    // Special handling for postal code
    if (field === 'postalCode') {
      // Remove any non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Format as XXX-XXXX
      if (digitsOnly.length <= 3) {
        value = digitsOnly;
      } else {
        value = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 7)}`;
      }
    }
    
    // Special handling for phone number
    if (field === 'phone') {
      // Remove any non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Format strictly as 0XX-XXXX-XXXX
      if (digitsOnly.length <= 3) {
        value = digitsOnly;
      } else if (digitsOnly.length <= 7) {
        value = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;
      } else {
        value = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 7)}-${digitsOnly.slice(7, 11)}`;
      }
      
      // Limit to 11 digits max
      if (digitsOnly.length > 11) {
        value = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 7)}-${digitsOnly.slice(7, 11)}`;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleCheckboxChange = () => {
    setFormData(prev => ({
      ...prev,
      isDefault: !prev.isDefault
    }));
  };

  const hasAddressChanged = () => {
    if (!isEditing || !address) return true;
    
    return (
      formData.lastName !== (address.lastname || '') ||
      formData.firstName !== (address.firstname || '') ||
      formData.lastNameKana !== (address.lastnameKana || '') ||
      formData.firstNameKana !== (address.firstnameKana || '') ||
      formData.phone !== (address.contactNumber || '') ||
      formData.postalCode !== (address.postalCode || '') ||
      formData.prefecture !== (address.prefecture || '') ||
      formData.city !== (address.city || '') ||
      formData.street !== (address.address1 || '') ||
      formData.building !== (address.address2 || '') ||
      formData.isDefault !== (address.isDefault || false)
    );
  };

  const handleSubmit = () => {
    // Validate form data
    const { isValid, errors: validationErrors } = validateAddressForm(formData);

    if (!isValid) {
      // Translate error messages
      const translatedErrors = {};
      Object.keys(validationErrors).forEach(key => {
        translatedErrors[key] = t(validationErrors[key]);
      });
      setErrors(translatedErrors);
      return;
    }

    // Only proceed if it's a new address or if there are changes
    if (!isEditing || hasAddressChanged()) {
      const formattedData = {
        lastname: formData.lastName,
        firstname: formData.firstName,
        lastnameKana: formData.lastNameKana,
        firstnameKana: formData.firstNameKana,
        contactNumber: formData.phone,
        postalCode: formData.postalCode,
        prefecture: formData.prefecture,
        city: formData.city,
        address1: formData.street,
        address2: formData.building,
        isDefault: formData.isDefault
      };
      onSave(formattedData, isEditing ? address.id : null);
    } else {
      onClose();
    }
  };

  const handleAutoFill = async () => {
    if (!formData.postalCode || formData.postalCode.length < 7) {
      setErrors(prev => ({
        ...prev,
        postalCode: t('purchase.address.postal_code_invalid')
      }));
      return;
    }

    setIsAutoFilling(true);
    try {
      const response = await getAddressByPostalCode(formData.postalCode);
      
      if (response.data && response.data.length > 0) {
        const addressData = response.data[0];
        
        setFormData(prev => ({
          ...prev,
          prefecture: addressData.prefecture,
          city: addressData.city || '',
          street: addressData.town || ''
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          postalCode: t('purchase.address.postal_code_not_found')
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        postalCode: t('purchase.address.postal_code_error')
      }));
    } finally {
      setIsAutoFilling(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex flex-col justify-center items-end z-50">
      <div className="w-[480px] h-full bg-white flex flex-col justify-between items-start overflow-hidden">
        {/* Header */}
        <div className="self-stretch py-6 inline-flex justify-center items-center relative">
          <div className="inline-flex justify-start items-center gap-2">
            <h2 className="text-lg font-bold">
              {t('me.menu.address')}
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="w-6 h-6 absolute right-6 top-6 flex items-center justify-center"
          >
            <img src={closeIcon} alt="close" className="w-7 h-7" />
          </button>
        </div>

        {/* Form Content */}
        <div className="self-stretch flex-1 px-6 pb-6 overflow-y-auto">
          <div className="self-stretch flex flex-col justify-start items-start gap-6">
            {/* Personal Information Section */}
            <div className="self-stretch flex flex-col justify-start items-start gap-3">
              <div className="self-stretch flex flex-col justify-start items-start gap-3">
                <div className="self-stretch inline-flex justify-start items-start gap-4">
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                    <div className="self-stretch inline-flex justify-start items-center gap-3">
                      <div className="self-stretch inline-flex justify-start items-center gap-1">
                        <div className="text-sm text-[#111111]">{t('purchase.delivery_info.last_name')}</div>
                      </div>
                    </div>
                    <BasicInput
                      placeholder={t('purchase.delivery_info.last_name_placeholder')}
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      containerClassName="self-stretch"
                      className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                      error={errors.lastName}
                    />
                  </div>
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                    <div className="self-stretch inline-flex justify-start items-center gap-3">
                      <div className="self-stretch inline-flex justify-start items-center gap-1">
                        <div className="text-sm text-[#111111]">{t('purchase.delivery_info.first_name')}</div>
                      </div>
                    </div>
                    <BasicInput
                      placeholder={t('purchase.delivery_info.first_name_placeholder')}
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      containerClassName="self-stretch"
                      className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                      error={errors.firstName}
                    />
                  </div>
                </div>

                <div className="self-stretch inline-flex justify-start items-start gap-4">
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                    <div className="self-stretch inline-flex justify-start items-center gap-3">
                      <div className="self-stretch inline-flex justify-start items-center gap-1">
                        <div className="text-sm text-[#111111]">{t('purchase.delivery_info.last_name_kana')}</div>
                      </div>
                    </div>
                    <BasicInput
                      placeholder={t('purchase.delivery_info.last_name_kana_placeholder')}
                      value={formData.lastNameKana}
                      onChange={(e) => handleInputChange('lastNameKana', e.target.value)}
                      containerClassName="self-stretch"
                      className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                      error={errors.lastNameKana}
                    />
                  </div>
                  <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
                    <div className="self-stretch inline-flex justify-start items-center gap-3">
                      <div className="self-stretch inline-flex justify-start items-center gap-1">
                        <div className="text-sm text-[#111111]">{t('purchase.delivery_info.first_name_kana')}</div>
                      </div>
                    </div>
                    <BasicInput
                      placeholder={t('purchase.delivery_info.first_name_kana_placeholder')}
                      value={formData.firstNameKana}
                      onChange={(e) => handleInputChange('firstNameKana', e.target.value)}
                      containerClassName="self-stretch"
                      className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                      error={errors.firstNameKana}
                    />
                  </div>
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-1">
                  <div className="self-stretch inline-flex justify-start items-start gap-3">
                    <div className="self-stretch inline-flex justify-start items-center gap-1">
                      <div className="text-sm text-[#111111]">{t('purchase.delivery_info.phone')}</div>
                    </div>
                  </div>
                  <BasicInput
                    placeholder={t('purchase.delivery_info.phone_placeholder')}
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    containerClassName="self-stretch"
                    className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                    error={errors.phone}
                  />
                </div>
              </div>
            </div>

            {/* Address Details Section */}
            <div className="self-stretch flex flex-col justify-start items-start gap-3">
              <div className="self-stretch flex flex-col justify-start items-start gap-3">
                <div className="self-stretch flex flex-col justify-end items-start gap-1.5">
                  <div className="self-stretch relative">
                    <div className="text-sm text-[#111111] mb-1">{t('purchase.address.postal_code')}</div>
                    <BasicInput
                      placeholder={t('purchase.address.postal_code_placeholder')}
                      value={formData.postalCode}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                      containerClassName="w-full"
                      prefix="〒"
                      prefixClassName="text-black"
                      error={errors.postalCode}
                    >
                      <div className={`absolute right-0 top-0 h-full bg-white border rounded-l-none rounded-r-md ${errors.postalCode ? 'border-[#ff5501]' : 'border-[#dddddd]'}`}>
                        <div 
                          className="h-full px-3 py-2.5 text-[#2966ff] text-sm content-center cursor-pointer"
                          onClick={handleAutoFill}
                          style={{ opacity: isAutoFilling || !formData.postalCode ? 0.5 : 1 }}
                        >
                          {isAutoFilling ? t('purchase.address.auto_filling') : t('purchase.address.auto_fill')}
                        </div>
                      </div>
                    </BasicInput>
                  </div>
                </div>
                
                <div className="self-stretch relative">
                  <div className="text-sm text-[#111111] mb-1">{t('purchase.address.prefecture')}</div>
                  <Select
                    options={prefectures}
                    value={formData.prefecture}
                    onValueChange={(value) => handleInputChange('prefecture', value)}
                    className="w-full h-12 text-start !rounded-md bg-[#f7f7f7]"
                    contentClassName="z-50"
                    placeholder={t('purchase.address.prefecture_placeholder')}
                    error={errors.prefecture}
                    disabled={isLoadingPrefectures}
                    maxVisibleItems={8} // Show up to 8 prefectures at once
                  />
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-1">
                  <div className="self-stretch inline-flex justify-start items-start gap-3">
                    <div className="self-stretch inline-flex justify-start items-center gap-1">
                      <div className="text-sm text-[#111111]">{t('purchase.address.city')}</div>
                    </div>
                  </div>
                  <BasicInput
                    placeholder={t('purchase.address.city_placeholder')}
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    containerClassName="self-stretch"
                    className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                    error={errors.city}
                  />
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-1">
                  <div className="self-stretch inline-flex justify-start items-start gap-3">
                    <div className="self-stretch inline-flex justify-start items-center gap-1">
                      <div className="text-sm text-[#111111]">{t('purchase.address.street')}</div>
                    </div>
                  </div>
                  <BasicInput
                    placeholder={t('purchase.address.street_placeholder')}
                    value={formData.street}
                    onChange={(e) => handleInputChange('street', e.target.value)}
                    containerClassName="self-stretch"
                    className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                    error={errors.street}
                  />
                </div>
              </div>
              
              <div className="self-stretch flex flex-col justify-start items-start gap-1">
                <div className="self-stretch inline-flex justify-start items-start gap-3">
                  <div className="self-stretch inline-flex justify-start items-center gap-1">
                    <div className="text-sm text-[#111111]">{t('purchase.address.building')}</div>
                  </div>
                </div>
                <BasicInput
                  placeholder={t('purchase.address.building_placeholder')}
                  value={formData.building}
                  onChange={(e) => handleInputChange('building', e.target.value)}
                  containerClassName="self-stretch"
                  className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                  error={errors.building}
                />
              </div>
            </div>

            {/* Set as Default */}
            {!hideDefaultOption && (
              <div className="inline-flex justify-start items-center gap-2">
                <Checkbox 
                  checked={formData.isDefault}
                  onCheckedChange={handleCheckboxChange}
                />
                <div className="text-[#666666] text-sm font-normal" onClick={handleCheckboxChange}>
                  {t('address.setAsDefault')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer with Save Button */}
        <div className="w-[480px] px-6 py-4 bg-white inline-flex justify-center items-center gap-4">
          <Button
            onClick={handleSubmit}
            variant="primary"
            size="xl"
            className="flex-1 h-14 px-7 bg-[#111111] rounded-[28px] flex justify-center items-center text-white"
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddressEditor; 